import { useMemo } from 'react';
import { USER_TYPE_OPTIONS, USER_STATUS_OPTIONS } from '../constants';
import { useGetIdentity } from '@refinedev/core';
import { formatPersonNameField } from '@src/utils/nameUtils';

export const useUsersFormFields = () => {
  const { data: currentUser } = useGetIdentity<any>();
  const isMasterAdmin = currentUser?.type === 'MASTER_ADMIN';

  return useMemo(
    () => [
      // Row 1: Email (Left) | Password (Right)
      {
        name: 'email',
        label: 'Email',
        type: 'email' as const,
        required: true,
        placeholder: 'Enter email address',
        layout: { span: 12 },
        props: {
          size: 'large',
          resourceAutoComplete: 'email',
        },
        tooltip: 'Enter a valid email address for the user',
      },
      {
        name: 'password',
        label: 'Password',
        type: 'password' as const,
        required: false,
        placeholder: 'Enter password',
        layout: { span: 12 },
        props: {
          size: 'large',
          resourceAutoComplete: 'new-password',
        },
        tooltip: 'Enter a secure password for the user',
      },

      // Row 2: First Name (Left) | Last Name (Right)
      {
        name: 'first_name',
        label: 'First Name',
        type: 'text' as const,
        required: true,
        placeholder: 'Enter first name',
        layout: { span: 12 },
        props: {
          size: 'large',
          resourceAutoComplete: 'given-name',
        },
        tooltip: 'Enter the user\'s first name',
        transform: {
          output: (value: any) => formatPersonNameField(value),
        },
      },
      {
        name: 'last_name',
        label: 'Last Name',
        type: 'text' as const,
        required: true,
        placeholder: 'Enter last name',
        layout: { span: 12 },
        props: {
          size: 'large',
          resourceAutoComplete: 'family-name',
        },
        tooltip: 'Enter the user\'s last name',
        transform: {
          output: (value: any) => formatPersonNameField(value),
        },
      },

      // Row 3: Mobile Number (Left) | User Type (Right)
      {
        name: 'mobile_no',
        label: 'Mobile Number',
        type: 'telephone' as const,
        required: true,
        placeholder: 'Enter mobile number',
        layout: { span: 12 },
        props: {
          size: 'large',
          resourceAutoComplete: 'tel',
        },
        tooltip: 'Enter the user\'s mobile number',
      },
      {
        name: 'type',
        label: 'User Type',
        type: 'select' as const,
        required: true,
        placeholder: 'Select user type',
        layout: { span: 12 },
        options: USER_TYPE_OPTIONS,
        props: {
          size: 'large',
        },
        tooltip: 'Select the user\'s role/type in the system',
      },

      // Row 4: User Status (Full Width)
      {
        name: 'status',
        label: 'User Status',
        type: 'select' as const,
        required: true,
        placeholder: 'Select user status',
        layout: { span: 24 },
        options: USER_STATUS_OPTIONS,
        props: {
          size: 'large',
        },
        tooltip: 'Select the user\'s current status',
      },
      // Only show organization field for Master Admin
      ...(isMasterAdmin ? [{
        name: 'organization_id',
        label: 'Organization',
        type: 'resourceSelect' as const,
        required: false,
        placeholder: 'Search and select organization',
        layout: { span: 12 },
        searchSelectProps: {
          resource: 'organizations',
          optionValue: 'id',
          searchFields: ['name', 'code'],
          searchDebounceMs: 300,
          pagination: { pageSize: 20 },
          sorters: [
            {
              field: 'name',
              order: 'asc',
            },
          ],
          queryOptions: {
            enabled: true,
            staleTime: 5 * 60 * 1000,
            cacheTime: 10 * 60 * 1000,
            refetchOnWindowFocus: false,
            refetchOnMount: true,
          },
        },
        props: {
          size: 'large',
          showSearch: true,
          allowClear: true,
        },
        tooltip: 'Select the organization this user belongs to',
      }] : []),
    ],
    [isMasterAdmin]
  );
};